<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from "vue";
import type { Progress } from "../api";

const props = defineProps<{ jobId: string | null }>();
const emit = defineEmits<{ done: [number]; progress: [Progress] }>();

const lines = ref<string[]>([]);
const box = ref<HTMLElement | null>(null);
let es: EventSource | null = null;

function close() {
  es?.close();
  es = null;
}

// Autoscroll tylko wtedy, gdy użytkownik JEST na dole. Inaczej przewinięcie w
// górę, żeby coś przeczytać w trakcie skanu, byłoby natychmiast cofane przez
// każdą nową linię — a linie lecą kilka razy na sekundę.
function atBottom(): boolean {
  const e = box.value;
  if (!e) return true;
  return e.scrollHeight - e.scrollTop - e.clientHeight < 40;
}

watch(
  () => props.jobId,
  (id) => {
    close();
    lines.value = [];
    if (!id) return;

    es = new EventSource(`/api/jobs/${encodeURIComponent(id)}/events`);
    es.addEventListener("log", async (e) => {
      const stick = atBottom();
      lines.value.push(JSON.parse((e as MessageEvent).data).line);
      if (stick) {
        await nextTick();
        if (box.value) box.value.scrollTop = box.value.scrollHeight;
      }
    });
    // Stan postępu ma własny kanał — rodzic pokazuje go nad logiem (ScanProgress).
    es.addEventListener("progress", (e) => {
      emit("progress", JSON.parse((e as MessageEvent).data) as Progress);
    });
    es.addEventListener("done", (e) => {
      const d = JSON.parse((e as MessageEvent).data) as { code: number };
      lines.value.push("");
      lines.value.push(`— zakończono (kod ${d.code}) —`);
      close();
      emit("done", d.code);
    });
    // Zerwane SSE zamykamy: bez tego przeglądarka wznawia je w kółko, a zadanie
    // i tak już nie żyje.
    es.onerror = () => close();
  },
  { immediate: true },
);

onBeforeUnmount(close);
</script>

<template>
  <details v-if="jobId || lines.length" class="logwrap" :open="!lines.length || undefined">
    <summary>Log techniczny ({{ lines.length }} linii)</summary>
    <pre ref="box" class="log">{{ lines.join("\n") }}</pre>
  </details>
</template>

<style scoped>
.logwrap > summary { cursor: pointer; color: var(--mut); font-size: 12px; user-select: none; }
.logwrap > summary:hover { color: var(--fg); }
.log {
  background: var(--logbg); color: #c9d6e6; border: 1px solid var(--bd);
  border-radius: var(--r2); padding: var(--s3); max-height: 340px; overflow: auto;
  white-space: pre-wrap; font: 12px/1.6 var(--mono); margin: var(--s2) 0 0;
}
</style>
